import { createRefMemo } from '@/shared/libs/create-ref-memo';
import type { GameCatalogItem } from './types';

export type Game = GameCatalogItem;

export const ALL_FILTER = 'all';
export const BONUS_FILTER = 'bonus';

interface TypeOrProviderContext {
  isTypeSelected: boolean;
  isProviderSelected: boolean | string;
  gameTypeOrProvider?: string;
}

export function matchesTypeOrProvider(game: Game, { isTypeSelected, isProviderSelected, gameTypeOrProvider }: TypeOrProviderContext): boolean {
  if (isTypeSelected) return game.type === gameTypeOrProvider;
  if (isProviderSelected) return gameTypeOrProvider === ALL_FILTER || game.providerId === gameTypeOrProvider;
  return true;
}

interface CategoryContext {
  isCategorySelected: boolean | string;
  filterParam?: string;
  isBonusActive: boolean;
  bonusGamesSet: Set<number>;
}

export function matchesCategory(game: Game, { isCategorySelected, filterParam, isBonusActive, bonusGamesSet }: CategoryContext): boolean {
  if (!isCategorySelected) return true;
  if (filterParam === BONUS_FILTER && isBonusActive) {
    return bonusGamesSet.has(game?.id);
  }
  return Boolean(filterParam && game?.filters?.includes(filterParam));
}

interface SearchContext {
  isSearchTyped: boolean | string;
  searchParam?: string;
}

export function matchesSearch(game: Game, { isSearchTyped, searchParam }: SearchContext): boolean {
  return !isSearchTyped || game.title.toLowerCase().includes((searchParam ?? '').toLowerCase());
}

interface WishlistContext {
  isWishlist: boolean | string;
  wishlistSet: Set<number>;
}

export function matchesWishlist(game: Game, { isWishlist, wishlistSet }: WishlistContext): boolean {
  return !isWishlist || wishlistSet.has(game.id);
}

export const getWishlistSet = createRefMemo((wishlist: number[] | undefined) => new Set(wishlist ?? []));
export const getGameById = createRefMemo((gameData: Game[] | undefined) => new Map((gameData ?? []).map((game) => [game.id, game])));

let lastFilteredGamesDeps: unknown[] | null = null;
let lastFilteredGamesResult: Game[] | null = null;

export function getFilteredGames(compute: () => Game[], deps: unknown[]): Game[] {
  if (
    lastFilteredGamesDeps &&
    lastFilteredGamesDeps.length === deps.length &&
    lastFilteredGamesDeps.every((dep, i) => Object.is(dep, deps[i]))
  ) {
    return lastFilteredGamesResult!;
  }

  lastFilteredGamesResult = compute();
  lastFilteredGamesDeps = deps;
  return lastFilteredGamesResult;
}
